<template>
    <Modal
        :is-open="showRedemptionEditModal"
        :title="
            editingUnfiledRedemption ? 'Review redemption' : 'Edit redemption'
        "
        @close="tripsStore.closeRedemptionEditModal()"
        :full-screen-mobile="true"
    >
        <template #subtitle>
            <div class="text-slate-500 dark:text-slate-400 text-sm leading-5 font-normal">
                <template v-if="editingUnfiledRedemption">
                    <!-- No subtitle for unfiled redemptions per Figma -->
                </template>
                <template v-else-if="trip">
                    {{ trip?.title }},
                    {{ formatDateRange(trip?.start_date, trip?.end_date) }}
                </template>
                <template
                    v-if="
                        selectedHotel &&
                        hotelStartDate &&
                        redemptionType?.slug === 'hotel'
                    "
                >
                    <span v-if="trip">⋅</span>
                    {{ selectedHotel }},
                    {{ formatDateRange(hotelStartDate, hotelEndDate) }}
                </template>
                <template
                    v-if="
                        selectedFlight &&
                        flightStartDate &&
                        redemptionType?.slug === 'flight'
                    "
                >
                    <span v-if="trip">⋅</span>
                    {{ selectedFlight }},
                    {{ formatDateRange(flightStartDate, flightEndDate) }}
                </template>
                <template
                    v-if="
                        selectedTrain &&
                        trainStartDate &&
                        redemptionType?.slug === 'train'
                    "
                >
                    <span v-if="trip">⋅</span>
                    {{ selectedTrain }},
                    {{ formatDateRange(trainStartDate, trainEndDate) }}
                </template>
                <template
                    v-if="
                        selectedRentalCar &&
                        rentalCarStartDate &&
                        redemptionType?.slug === 'rental-car'
                    "
                >
                    <span v-if="trip">⋅</span>
                    {{ selectedRentalCar }},
                    {{ formatDateRange(rentalCarStartDate, rentalCarEndDate) }}
                </template>
                <template
                    v-if="
                        selectedOther &&
                        otherStartDate &&
                        redemptionType?.slug === 'other'
                    "
                >
                    <span v-if="trip">⋅</span>
                    {{ selectedOther }},
                    {{ formatDateRange(otherStartDate, otherEndDate) }}
                </template>
            </div>
        </template>
        <template #content>
            <div class="mb-8">
                <!-- Unfiled: missing details banner -->
                <div
                    v-if="editingUnfiledRedemption && missingFields.length > 0"
                    class="mb-6 w-full p-4 rounded-lg bg-[#F1F5F9] dark:bg-slate-700"
                >
                    <div class="flex items-start gap-3 flex-1">
                        <div
                            class="flex items-start justify-center w-9 h-9 shrink-0"
                        >
                            <Icon
                                icon="circle-i"
                                class="text-slate-900 dark:text-slate-100 fill-slate-800"
                                :width="24"
                                :height="24"
                            />
                        </div>
                        <div class="flex flex-col">
                            <p
                                class="text-sm font-medium leading-5 text-[#1E293B] dark:text-slate-100"
                            >
                                Missing required details:
                            </p>
                            <p
                                class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400"
                            >
                                {{ missingFields.join(', ') }}
                            </p>
                        </div>
                    </div>
                </div>

                <RedemptionStep2 />
                <RedemptionStep3 />
            </div>
        </template>
        <!-- Removed errors template to prevent duplicate error display -->
        <template #footer>
            <!-- Unfiled footer: Cancel, Save only, Save & Add to trip -->
            <div
                v-if="editingUnfiledRedemption"
                class="flex justify-end items-center gap-4"
            >
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="tripsStore.closeRedemptionEditModal()"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-secondary"
                    :disabled="missingFields.length > 0"
                    @click="handleSaveOnly"
                >
                    Save only
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="missingFields.length > 0"
                    @click="handleSaveAndAddToTrip"
                >
                    Save & Add to trip
                </button>
            </div>

            <!-- Normal edit footer -->
            <div v-else class="flex justify-end items-center gap-4">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="tripsStore.closeRedemptionEditModal()"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-primary"
                    @click="handleSave"
                >
                    Save Changes
                </button>
            </div>
        </template>
    </Modal>
</template>
<script setup>
import { onMounted, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import Modal from '@/components/ui/modal/ui-modal.vue';
import RedemptionStep2 from '@/components/trips/modals/steps/redemption-step-2.vue';
import RedemptionStep3 from '@/components/trips/modals/steps/redemption-step-3.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { notify } from 'notiwind';
import dayjs from 'dayjs';

const tripsStore = useTripsStore();

const {
    showRedemptionEditModal,
    trip,
    activeRedemption,
    redemptionType,
    editingUnfiledRedemption,
} = storeToRefs(tripsStore);

// Compute missing fields for unfiled banner
const missingFields = computed(() => {
    if (!editingUnfiledRedemption.value || !activeRedemption.value) return [];
    const fields = [];
    const type = redemptionType.value?.slug;

    if (type === 'flight') {
        if (!activeRedemption.value.start_date) fields.push('Flight date');
        const segments = activeRedemption.value.travel_segments || [];
        if (segments.length === 0 || !segments[0]?.origin)
            fields.push('Origin');
        if (
            segments.length === 0 ||
            !segments[segments.length - 1]?.destination
        )
            fields.push('Destination');
    } else if (type === 'hotel') {
        if (!activeRedemption.value.name) fields.push('Hotel name');
        if (!activeRedemption.value.start_date) fields.push('Check in date');
    } else if (type === 'train') {
        if (!activeRedemption.value.start_date) fields.push('Train date');
        const segments = activeRedemption.value.travel_segments || [];
        if (segments.length === 0 || !segments[0]?.origin)
            fields.push('Origin');
        if (
            segments.length === 0 ||
            !segments[segments.length - 1]?.destination
        )
            fields.push('Destination');
    } else if (type === 'rental-car') {
        if (!activeRedemption.value.start_date) fields.push('Pickup date');
    } else {
        if (!activeRedemption.value.name) fields.push('Name');
        if (!activeRedemption.value.start_date) fields.push('Date');
    }

    // Payment fields validation
    const paymentType = activeRedemption.value.payment_type;
    if (paymentType === 'points' || paymentType === 'cash_points') {
        if (!activeRedemption.value.payment_program_id)
            fields.push('Points currency');
        if (!activeRedemption.value.payment_points) fields.push('Points used');
    }
    if (paymentType === 'cash_points') {
        if (!activeRedemption.value.payment_cash_amount)
            fields.push('Cash amount');
    }

    return fields;
});

const validateRedemption = () => {
    const errors = {};
    const type = redemptionType.value?.slug;
    const segments = activeRedemption.value?.travel_segments || [];

    if (type === 'flight' || type === 'train') {
        if (!activeRedemption.value?.start_date) {
            errors['start_date'] =
                type === 'flight'
                    ? 'Flight date is required'
                    : 'Travel date is required';
        }
        if (segments.length === 0 || !segments[0]?.origin) {
            errors['origin'] = 'Origin is required';
        }
        if (
            segments.length === 0 ||
            !segments[segments.length - 1]?.destination
        ) {
            errors['destination'] = 'Destination is required';
        }
    } else if (type === 'hotel') {
        if (!activeRedemption.value?.name) {
            errors['name'] = 'Hotel name is required';
        }
        if (!activeRedemption.value?.start_date) {
            errors['start_date'] = 'Check in date is required';
        }
    } else if (type === 'rental-car') {
        if (!activeRedemption.value?.start_date) {
            errors['start_date'] = 'Pickup date is required';
        }
    } else {
        if (!activeRedemption.value?.start_date) {
            errors['start_date'] = 'Date is required';
        }
    }

    // Payment fields validation (only for unfiled redemptions)
    if (editingUnfiledRedemption.value) {
        const paymentType = activeRedemption.value?.payment_type;
        if (paymentType === 'points' || paymentType === 'cash_points') {
            if (!activeRedemption.value?.payment_program_id) {
                errors['payment_program_id'] = 'Points currency is required';
            }
        }
    }

    tripsStore.tripFormErrors = errors;
    return Object.keys(errors).length === 0;
};

// Trigger validation immediately when modal opens for unfiled redemptions
watch(
    showRedemptionEditModal,
    (isOpen) => {
        if (isOpen && editingUnfiledRedemption.value) {
            validateRedemption();
        }
    },
    { immediate: true }
);

// Normal edit save (non-unfiled)
const handleSave = () => {
    if (!validateRedemption()) return;
    tripsStore.updateRedemption();
};

// Unfiled: Save only (save without strict validation, keep unfiled)
const handleSaveOnly = async () => {
    if (!validateRedemption()) return;
    const response = await tripsStore.updateRedemption({
        silent: true,
        attachmentsOnly: true,
    });
    if (response && response.status === 200) {
        notify({
            group: 'success',
            title: 'Changes saved',
            text: 'Changes were successfully saved.',
        });
    }
};

// Unfiled: Save & Add to trip (save, close edit modal, then open attach modal)
const handleSaveAndAddToTrip = async () => {
    if (!validateRedemption()) return;
    const response = await tripsStore.updateRedemption({
        silent: true,
        keepEditModalOpen: true,
        attachmentsOnly: true,
    });

    if (!response || response.status !== 200) {
        return;
    }

    const savedRedemption = response.data.redemption;
    tripsStore.setAttachAfterEditRedemption(savedRedemption);
    tripsStore.closeRedemptionEditModal();
};

const getCityName = (airport) => {
    if (!airport) return '';
    const match = airport.match(/^([^,(-]+)/);
    return match ? match[1].trim() : airport;
};

const selectedFlight = computed(() => {
    if (
        redemptionType.value?.slug !== 'flight' ||
        !activeRedemption.value?.travel_segments?.length
    )
        return null;
    const segment = activeRedemption.value.travel_segments[0];
    if (!segment.origin || !segment.destination) return null;

    const originCity = getCityName(segment.origin_name || segment.origin);
    const destinationCity = getCityName(
        segment.destination_name || segment.destination
    );

    return `Flight ${originCity} - ${destinationCity}`;
});

const selectedTrain = computed(() => {
    if (
        redemptionType.value?.slug !== 'train' ||
        !activeRedemption.value?.travel_segments?.length
    )
        return null;
    const segment = activeRedemption.value.travel_segments[0];
    if (!segment.origin || !segment.destination) return null;

    const originCity = getCityName(segment.origin_name || segment.origin);
    const destinationCity = getCityName(
        segment.destination_name || segment.destination
    );

    return `Train ${originCity} – ${destinationCity}`;
});

const flightStartDate = computed(() => {
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return (
        activeRedemption.value.travel_segments[0].departure_date ||
        activeRedemption.value?.start_date ||
        ''
    );
});

const flightEndDate = computed(() => {
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return (
        activeRedemption.value.travel_segments[0].arrival_date ||
        activeRedemption.value?.end_date ||
        ''
    );
});

const trainStartDate = computed(() => {
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return (
        activeRedemption.value.travel_segments[0].departure_date ||
        activeRedemption.value?.start_date ||
        ''
    );
});

const trainEndDate = computed(() => {
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return (
        activeRedemption.value.travel_segments[0].arrival_date ||
        activeRedemption.value?.end_date ||
        ''
    );
});

const selectedRentalCar = computed(() => {
    if (redemptionType.value?.slug !== 'rental-car') return null;
    return `Rental car ${activeRedemption.value.pickup_location || ''} – ${activeRedemption.value.dropoff_location || ''}`;
});

const rentalCarStartDate = computed(() => {
    return activeRedemption.value?.start_date || '';
});

const rentalCarEndDate = computed(() => {
    return activeRedemption.value?.end_date || '';
});

const selectedOther = computed(() => {
    if (redemptionType.value?.slug !== 'other') return null;
    return activeRedemption.value.name || '';
});

const otherStartDate = computed(() => {
    return activeRedemption.value?.start_date || '';
});

const otherEndDate = computed(() => {
    return activeRedemption.value?.end_date || '';
});

const formatDateRange = (start, end) => {
    if (!start) return '';
    const startDate = dayjs(start);
    if (!end || start === end) {
        return startDate.format('MMM D, YYYY');
    }
    const endDate = dayjs(end);
    if (startDate.year() === endDate.year()) {
        return `${startDate.format('MMM D')} – ${endDate.format('MMM D, YYYY')}`;
    }
    return `${startDate.format('MMM D, YYYY')} – ${endDate.format('MMM D, YYYY')}`;
};

const selectedHotel = computed(() => {
    return activeRedemption.value?.name || '';
});

const hotelStartDate = computed(() => {
    return activeRedemption.value?.start_date || '';
});

const hotelEndDate = computed(() => {
    return activeRedemption.value?.end_date || '';
});

onMounted(() => {
    tripsStore.setRedemptionStep(3);
});
</script>
